/**
 * Apache Parquet export settings: defaults, the columns editor's text format and
 * validation. Kept free of Vue so it can be reused and tested on its own.
 * @module data-export/apache-parquet
 */

/**
 * Arrow types the node service can write, spelled as in destination contracts.
 * @type {string[]}
 */
export const ARROW_TYPES = ['string', 'int32', 'int64', 'float64', 'bool', 'date32', 'timestamp[us, tz=UTC]'];

// Other ways people and contracts write the same types.
const TYPE_ALIASES = {
  text: 'string',
  varchar: 'string',
  str: 'string',
  int: 'int32',
  integer: 'int32',
  int4: 'int32',
  bigint: 'int64',
  int8: 'int64',
  long: 'int64',
  double: 'float64',
  float: 'float64',
  float8: 'float64',
  numeric: 'float64',
  decimal: 'float64',
  boolean: 'bool',
  date: 'date32',
  timestamp: 'timestamp[us, tz=UTC]',
  timestamptz: 'timestamp[us, tz=UTC]',
  datetime: 'timestamp[us, tz=UTC]',
};

// Arrow type suggested for each Postgres type a tool's query returns.
const DB_TYPE_TO_ARROW = {
  int2: 'int32',
  int4: 'int32',
  int8: 'int64',
  float4: 'float64',
  float8: 'float64',
  numeric: 'float64',
  money: 'float64',
  bool: 'bool',
  date: 'date32',
  timestamp: 'timestamp[us, tz=UTC]',
  timestamptz: 'timestamp[us, tz=UTC]',
};

// Arrow type suggested from a tool's DataTable column type, when the database type is unknown.
const PLATFORM_TYPE_TO_ARROW = { Number: 'float64', YesNo: 'bool', Date: 'timestamp[us, tz=UTC]' };

const NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
const HEADER_NAMES = ['column', 'name', 'column name', 'field'];

/**
 * Settings for a newly chosen Apache Parquet export.
 * @returns {{path: string, partition_column: string, columns: object[]}} Empty settings.
 */
export function defaultConfig() {
  return { path: '', partition_column: '', columns: [] };
}

/**
 * Finds the Arrow type in the text after a column's name: an exact Arrow type anywhere,
 * or a known alias as the first word.
 * @param {string} rest The line without the column name, e.g. "`float64`, nullable".
 * @returns {{type?: string, word?: string}} The Arrow type, or the unrecognised first word.
 */
function findType(rest) {
  const exact = ARROW_TYPES.slice()
    .sort((a, b) => b.length - a.length)
    .find((type) => new RegExp(`(^|[^\\w])${type.replace(/[[\]]/g, '\\$&')}($|[^\\w])`).test(rest));
  if (exact) return { type: exact };
  const word = (rest.match(/[A-Za-z]\w*/) || [''])[0].toLowerCase();
  return TYPE_ALIASES[word] ? { type: TYPE_ALIASES[word] } : { word };
}

/**
 * Reads one line of the columns editor's text view. A line is a contract table row,
 * e.g. "| `Customer_ID` | `int64` |", or "Name type", optionally followed by
 * "nullable", "not null" or "optional" (may be missing from the query, allows nulls).
 * @param {string} line One line of text.
 * @returns {{column?: {name: string, type: string, nullable: boolean, required: boolean}, error?: string}|null} The column,
 *   a readable error, or null for blank, header and separator lines.
 */
export function parseColumnLine(line) {
  const text = String(line || '')
    .replace(/`/g, '')
    .trim();
  if (!text || /^[\s|:-]+$/.test(text)) return null;

  let name;
  let rest;
  if (text.includes('|')) {
    const cells = text
      .split('|')
      .map((cell) => cell.trim())
      .filter(Boolean);
    [name] = cells;
    rest = cells.slice(1).join(' ');
  } else {
    [, name, rest] = text.match(/^([^\s:,]+)[\s:,]*(.*)$/) || [];
  }
  name = (name || '').trim();
  rest = (rest || '').trim();
  if (HEADER_NAMES.includes(name.toLowerCase())) return null;
  if (!NAME_PATTERN.test(name)) return { error: `"${name}" is not a valid column name. Use letters, numbers and underscores, not starting with a number.` };
  if (!rest) return { error: `${name} has no type. Add one of: ${ARROW_TYPES.join(', ')}.` };

  const { type, word } = findType(rest);
  if (!type) return { error: `${name} has an unknown type "${word || rest}". Use one of: ${ARROW_TYPES.join(', ')}.` };

  const flags = rest.toLowerCase();
  const optional = /\boptional\b/.test(flags);
  const notNull = /\bnot null\b|\bnon-null\b|\bnever null\b/.test(flags);
  const nullable = optional || (!notNull && /\bnullable\b|\bnull\b/.test(flags));
  return { column: { name, type, nullable, required: !optional } };
}

/**
 * Reads the columns editor's text view, line by line.
 * @param {string} text One column per line.
 * @returns {{columns: object[], issues: Array<{line: number, message: string}>}} The valid columns in order, and a readable issue per bad line.
 */
export function parseColumnText(text) {
  const columns = [];
  const issues = [];
  const firstLine = {};
  String(text || '')
    .split('\n')
    .forEach((line, index) => {
      const parsed = parseColumnLine(line);
      if (!parsed) return;
      if (parsed.error) {
        issues.push({ line: index + 1, message: parsed.error });
        return;
      }
      const { name } = parsed.column;
      if (firstLine[name]) {
        issues.push({ line: index + 1, message: `${name} is already on line ${firstLine[name]}.` });
        return;
      }
      firstLine[name] = index + 1;
      columns.push(parsed.column);
    });
  return { columns, issues };
}

/**
 * Writes columns in the text view's format, so the two views convert both ways.
 * @param {object[]} columns Columns: { name, type, nullable, required }.
 * @returns {string} One line per column, e.g. "Closing_Cash_Balance float64 nullable".
 */
export function formatColumnText(columns) {
  return (columns || [])
    .map(({ name, type, nullable, required }) => {
      let flags = '';
      if (required === false) flags = ' optional';
      else if (nullable) flags = ' nullable';
      return `${name} ${type}${flags}`;
    })
    .join('\n');
}

/**
 * Turns a tool's output columns into block columns, suggesting each Arrow type from
 * the database type the query returns. Columns allow nulls until the user says otherwise.
 * @param {Array<{name: string, type?: string, db_type?: string}>} outputColumns The tool's output columns.
 * @returns {Array<{name: string, type: string, nullable: boolean, required: boolean}>} Block columns.
 */
export function columnsFromTool(outputColumns) {
  return (outputColumns || [])
    .filter((column) => column?.name)
    .map((column) => ({
      name: column.name,
      type: DB_TYPE_TO_ARROW[column.db_type] || PLATFORM_TYPE_TO_ARROW[column.type] || 'string',
      nullable: true,
      required: true,
    }));
}

/**
 * Whether Apache Parquet settings are complete: a path, uniquely named columns, and a
 * date32 partition column with a partition placeholder in the path when partitioned.
 * @param {object} [config={}] The settings (`parquet_config`).
 * @returns {boolean} True when the export can run.
 */
export function validate(config = {}) {
  if (!config.path?.trim()) return false;
  const columns = config.columns || [];
  const names = columns.map((column) => column.name?.trim());
  if (!names.length || names.some((name) => !name) || new Set(names).size !== names.length) return false;
  if (config.partition_column) {
    if (!columns.some((column) => column.name === config.partition_column && column.type === 'date32')) return false;
    if (!/\{partition_(date|yyyymmdd)\}/.test(config.path)) return false;
  }
  return true;
}
